
import { CouponsWithImageUrl } from '@/lib/api/coupons.api';
import { BatchData } from '@/models/batch-data.model';
import { useScannerCouponStore } from '@/store/useScannerCoupon';
import { toRef, computed, PropType } from 'vue';
import { storeToRefs } from 'pinia';
export default {
  name: 'coupon-data',
  props: {
    item: {
      required: true,
      type: Object as PropType<CouponsWithImageUrl>,
    },
    batchData: {
      type: Object as PropType<BatchData>,
      default: {} as BatchData,
    },
  },
  setup(props) {
    const scannerCoupon = useScannerCouponStore();
    const { currentScanner } = storeToRefs(scannerCoupon);
    const item = toRef(props, 'item');

    const batchId = computed(() => item.value.batchId || props.batchData.batchId);
    /**
     * Truthy when the coupon has a resolvable driver id, either as a PG
     * `drivers.id` (legacy / 200-path scanner / app-membre) or as an Odoo
     * `res.partner.id` (404-path scanner). Used only as a truthiness signal
     * for `v-if` checks; the human-readable string is derived elsewhere
     * (see `driverData` for licence text and `erpDriverPartnerId` for the
     * Odoo id used by ERP links).
     */
    const driverId = computed(
      () =>
        item.value.driverId ??
        item.value.odooDriverId ??
        props.batchData.driverId ??
        props.batchData.odooDriverId,
    );

    /**
     * Truthy when the coupon has a resolvable vehicle id, either as a
     * PG-aligned `vehicleId` or as the new raw Odoo fleet id `fleetVehicleId`.
     * Used only for `v-if` truthiness; display text is derived from
     * `coupon.vehicle.plateNumber` or `batchData.vehicle.vignette_id`.
     */
    const vehicleId = computed(
      () =>
        item.value.vehicleId ??
        item.value.fleetVehicleId ??
        props.batchData.vehicleId ??
        props.batchData.fleetVehicleId,
    );

    /** Show permis / véhicule / ERP row: not only after batch — also after licence lookup (Odoo id) or when coupon already has driver. */
    const showCouponContextRow = computed(() => {
      if (currentScanner.value.isAppMembre) {
        return true;
      }
      if (batchId.value) {
        return true;
      }
      if (props.batchData.driverOdooId != null) {
        return true;
      }
      if (props.batchData.driverLicence != null && String(props.batchData.driverLicence).length > 0) {
        return true;
      }
      if (item.value.driverId != null) {
        return true;
      }
      if (item.value.odooDriverId != null) {
        return true;
      }
      const d = item.value.driver as { odooId?: number; odoo_id?: number } | undefined;
      if (d != null && (d.odooId != null || d.odoo_id != null)) {
        return true;
      }
      return false;
    });

    const erpDriverPartnerId = computed((): number | null => {
      const d = item.value.driver as { odooId?: number; odoo_id?: number } | undefined;
      const raw =
        d?.odooId ??
        d?.odoo_id ??
        item.value.odooDriverId ??
        props.batchData.driverOdooId ??
        props.batchData.odooDriverId;
      if (raw == null || raw === '') {
        return null;
      }
      const n = typeof raw === 'number' ? raw : Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    });
    /** Licence string when known; otherwise driver pocket; otherwise Odoo partner id — never the internal driver PK. */
    const driverData = computed(() => {
      const licence = props.batchData.driverLicence;
      if (licence != null && String(licence).length > 0) {
        return licence;
      }
      const pocket = (item.value.driver as { pocket?: string | null } | undefined)?.pocket;
      if (pocket != null && String(pocket).length > 0) {
        return String(pocket);
      }
      const odoo = erpDriverPartnerId.value;
      if (odoo != null) {
        return String(odoo);
      }
      return '';
    });
    const vehicleData = computed(() => {
      if (props.batchData.vehicle) {
        return props.batchData.vehicle.vignette_id;
      }
      const v = item.value.vehicle;
      return v?.plateNumber ?? vehicleId.value ?? '';
    });

    return {
      currentScanner,
      showCouponContextRow,
      batchId,
      driverId,
      erpDriverPartnerId,
      vehicleId,
      driverData,
      vehicleData,
    };
  }
}
